import React, { useEffect, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
// Dynamically import Leaflet in browser via ESM to avoid require() errors
let L: any;
let loadLeafletPromise: Promise<void> | null = null;
async function ensureLeafletLoaded(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (L) return;
  if (!loadLeafletPromise) {
    loadLeafletPromise = (async () => {
      const leaflet = await import('leaflet');
      L = leaflet.default || leaflet;
      // Expose to window for plugins expecting global L
      (window as any).L = L;
      await import('leaflet-draw');
      // Fix default marker icons
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      });
    })();
  }
  await loadLeafletPromise;
}
import { ChevronDown, Square, Trash2 } from 'lucide-react';

// (marker icon fix moved into ensureLeafletLoaded)

interface Property {
  id: string;
  title: string;
  price: number;
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  state?: string;
  bedrooms?: number;
  bathrooms?: number;
  images?: string[];
}

interface PropertyMapProps {
  properties: Property[];
  selectedPropertyId?: string;
  onPropertySelect?: (property: Property) => void;
  onBoundsChange?: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  onDrawnArea?: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null) => void;
  height?: string;
  className?: string;
  targetBounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null;
  modalOpen?: boolean;
}

const PropertyMap: React.FC<PropertyMapProps> = ({ 
  properties, 
  selectedPropertyId,
  onPropertySelect,
  onBoundsChange,
  onDrawnArea,
  height = '500px', 
  className = '',
  targetBounds = null,
  modalOpen = false,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const currentTileLayerRef = useRef<L.TileLayer | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const drawControlRef = useRef<L.Control.Draw | null>(null);
  const drawnLayersRef = useRef<L.FeatureGroup | null>(null);
  const [mapType, setMapType] = useState<'automatic' | 'satellite'>('automatic');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDrawMode, setIsDrawMode] = useState(false);
  const didFitBoundsRef = useRef<boolean>(false);
  const originalInteractionState = useRef<{ dragging: boolean; touchZoom: boolean; scrollWheelZoom: boolean; doubleClickZoom: boolean; boxZoom: boolean; keyboard: boolean; tap?: boolean } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!mapRef.current || mapInstanceRef.current) return;
    let cancelled = false;
    (async () => {
      await ensureLeafletLoaded();
      if (cancelled || !L || !mapRef.current || mapInstanceRef.current) return;

    // Initialize map only once - center on Miami Beach for faster initial load
    const map = L.map(mapRef.current).setView([25.7907, -80.1300], 13);
    mapInstanceRef.current = map;

    // Add modern tile layer for map
    const automaticLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
      className: 'map-tiles-automatic'
    });
    
    automaticLayer.addTo(map);
    currentTileLayerRef.current = automaticLayer;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      map.removeLayer(marker);
    });
    markersRef.current.clear();

    // Add property markers (waterfront-only guard on client-side as well)
    const markers: L.Marker[] = [];
    const validProperties = properties.filter(p => p.latitude && p.longitude && (p as any).isWaterfront !== false);

    validProperties.forEach(property => {
      // Always start with red dot marker
      const redIcon = L.divIcon({
        html: `
          <div class="red-dot-marker" style="
            background: #dc2626;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            cursor: pointer;
          "></div>
        `,
        className: 'custom-red-marker',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });

      const lat = typeof property.latitude === 'string' ? parseFloat(property.latitude) : property.latitude!;
      const lng = typeof property.longitude === 'string' ? parseFloat(property.longitude) : property.longitude!;
      
      const marker = L.marker([lat, lng], {
        icon: redIcon
      }).addTo(map);

      // Store marker reference with property ID
      markersRef.current.set(property.id, marker);

      // Add click handler to fetch full gallery details and update popup
      marker.on('click', async function() {
        // Show quick loading state immediately
        try {
          marker.setPopupContent(`
            <div style=\"min-width: 280px; max-width: 560px; padding:8px;\">
              <div style=\"display:flex;align-items:center;gap:8px;color:#555;\">
                <div style=\"width:14px;height:14px;border:2px solid #999;border-top-color:transparent;border-radius:9999px;animation:spin 1s linear infinite;\"></div>
                Loading photos...
              </div>
            </div>
            <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
          `);
          marker.openPopup();
        } catch {}

        try {
          const detailsId = property.id;
          const resp = await fetch(`/api/properties/${detailsId}/details`, { cache: 'no-store', headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' } });
          if (resp.ok) {
            const detailed = await resp.json();
            const imgs: string[] = (Array.isArray(detailed.photos) && detailed.photos.length)
              ? detailed.photos
              : (Array.isArray(detailed.images) ? detailed.images : []);
            console.log('📸 Popup (property-map) fetched images:', { id: detailsId, count: imgs.length });
            const galleryHtml = (imgs && imgs.length)
              ? `<div style="position: relative; margin-bottom: 8px;">
                   <div style="white-space: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; scroll-snap-type: x mandatory;">
                     ${imgs.map((url: string) => `
                       <img src="${url}"
                            style="display:inline-block; width: 280px; height: 170px; object-fit: cover; border-radius: 8px; margin-right: 8px; scroll-snap-align: start;"
                            loading="lazy" decoding="async"
                            onerror="this.style.display='none';" />
                     `).join('')}
                   </div>
                   <div style="position:absolute; top:50%; left:4px; transform: translateY(-50%); background:rgba(0,0,0,0.5); color:white; width:24px; height:24px; border-radius:9999px; display:flex; align-items:center; justify-content:center; cursor:pointer; user-select:none;" onclick="this.parentElement.querySelector('div').scrollBy({left:-260, behavior:'smooth'})">‹</div>
                   <div style="position:absolute; top:50%; right:4px; transform: translateY(-50%); background:rgba(0,0,0,0.5); color:white; width:24px; height:24px; border-radius:9999px; display:flex; align-items:center; justify-content:center; cursor:pointer; user-select:none;" onclick="this.parentElement.querySelector('div').scrollBy({left:260, behavior:'smooth'})">›</div>
                 </div>`
              : '';

            const newContent = `
              <div style=\"min-width: 280px; max-width: 560px;\">
                ${galleryHtml}
                <h3 style=\"margin: 0 0 8px 0; color: #006BA6; font-size: 18px; font-weight: bold;\">${formatPrice(detailed.price)}</h3>
                <p style=\"margin: 0 0 4px 0; font-size: 14px; color: #666;\">${detailed.bedrooms || 'N/A'} bed, ${detailed.bathrooms || 'N/A'} bath</p>
                <p style=\"margin: 0 0 8px 0; font-size: 12px; color: #888;\">${detailed.address || `${detailed.city}, ${detailed.state}`} </p>
                <div style=\"display: flex; gap: 6px; margin-top: 8px;\">
                  <button onclick=\"window.location.href='/property/${property.id}'\" style=\"background:#006BA6;color:white;border:none;padding:6px 12px;border-radius:6px;cursor:pointer;font-size:11px;flex:1;\">View Details</button>
                </div>
                <p style=\"margin: 8px 0 0 0; font-size: 11px; color: #666; text-align: center;\">Click marker to view details and contact options</p>
              </div>`;
            marker.setPopupContent(newContent);
            marker.openPopup();
          }
        } catch {}
        if (onPropertySelect) {
          onPropertySelect(property);
        }
      });

      // Create popup content with ALL listing images (horizontal scroll, no limit)
      const galleryHtml = (property.images && property.images.length)
        ? `<div style=\"position: relative; margin-bottom: 8px;\">
             <div style=\"white-space: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; scroll-snap-type: x mandatory;\">
               ${property.images.map((url) => `
                 <img src=\"${url}\"
                      style=\"display:inline-block; width: 260px; height: 160px; object-fit: cover; border-radius: 8px; margin-right: 8px; scroll-snap-align: start;\"
                      onerror=\"this.style.display='none';\" />
               `).join('')}
             </div>
             <div style=\"position:absolute; top:50%; left:4px; transform: translateY(-50%); background:rgba(0,0,0,0.5); color:white; width:24px; height:24px; border-radius:9999px; display:flex; align-items:center; justify-content:center; cursor:pointer; user-select:none;\" onclick=\"this.parentElement.querySelector('div').scrollBy({left:-240, behavior:'smooth'})\">‹</div>
             <div style=\"position:absolute; top:50%; right:4px; transform: translateY(-50%); background:rgba(0,0,0,0.5); color:white; width:24px; height:24px; border-radius:9999px; display:flex; align-items:center; justify-content:center; cursor:pointer; user-select:none;\" onclick=\"this.parentElement.querySelector('div').scrollBy({left:240, behavior:'smooth'})\">›</div>
           </div>`
        : '';

      const popupContent = `
        <div style="min-width: 260px; max-width: 520px;">
          ${galleryHtml}
          <h3 style="margin: 0 0 8px 0; color: #006BA6; font-size: 18px; font-weight: bold;">
            ${formatPrice(property.price)}
          </h3>
          <p style="margin: 0 0 4px 0; font-size: 14px; color: #666;">
            ${property.bedrooms || 'N/A'} bed, ${property.bathrooms || 'N/A'} bath
          </p>
          <p style="margin: 0 0 8px 0; font-size: 12px; color: #888;">
            ${property.address || `${property.city}, ${property.state}`}
          </p>
          <button 
            onclick="window.location.href='/property/${property.id}'" 
            style="
              background: #006BA6; 
              color: white; 
              border: none; 
              padding: 6px 12px; 
              border-radius: 6px; 
              cursor: pointer; 
              font-size: 12px;
              width: 100%;
            "
          >
            View Details
          </button>
        </div>
      `;

      marker.bindPopup(popupContent);
      markers.push(marker);
    });

    // Fit bounds on first successful marker render only
    if (markers.length > 0 && !didFitBoundsRef.current) {
      console.log('🗺️ Initial map setup - fitting bounds to show properties');
      const group = new L.FeatureGroup(markers);
      map.fitBounds(group.getBounds().pad(0.1));
      didFitBoundsRef.current = true;
    }

    // Initialize drawing controls
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnLayersRef.current = drawnItems;

    const drawControl = new L.Control.Draw({
      position: 'topleft',
      draw: {
        marker: false,
        circle: false,
        circlemarker: false,
        polyline: false,
        polygon: {
          allowIntersection: false,
          showArea: true,
          drawError: {
            color: '#e1e100',
            message: '<strong>Error:</strong> shape edges cannot cross!'
          },
          shapeOptions: {
            color: '#006BA6',
            weight: 3,
            opacity: 0.8,
            fillOpacity: 0.3
          }
        },
        rectangle: {
          shapeOptions: {
            color: '#006BA6',
            weight: 3,
            opacity: 0.8,
            fillOpacity: 0.3
          }
        }
      },
      edit: {
        featureGroup: drawnItems,
        remove: true
      }
    });

    drawControlRef.current = drawControl;

    // Handle drawing events
    map.on('draw:created', function (e: any) {
      const layer = e.layer;
      drawnItems.addLayer(layer);
      
      // Get bounds of drawn shape
      const bounds = layer.getBounds();
      if (onDrawnArea) {
        onDrawnArea({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest()
        });
      }
    });

    map.on('draw:deleted', function () {
      if (onDrawnArea) {
        onDrawnArea(null);
      }
    });

    // Add bounds change listener for dynamic property loading
    if (onBoundsChange) {
      const handleBoundsChange = () => {
        try {
          // Safety checks to prevent _leaflet_pos errors
          if (!map || !mapInstanceRef.current || !drawnItems) {
            return;
          }

          // Only update bounds if not in draw mode and no drawn areas exist
          if (!isDrawMode && drawnItems.getLayers().length === 0) {
            const bounds = map.getBounds();
            if (bounds) {
              console.log('🗺️ Map bounds changed:', {
                north: bounds.getNorth(),
                south: bounds.getSouth(),
                east: bounds.getEast(),
                west: bounds.getWest()
              });
              onBoundsChange({
                north: bounds.getNorth(),
                south: bounds.getSouth(),
                east: bounds.getEast(),
                west: bounds.getWest()
              });
            }
          }
        } catch (error) {
          console.warn('Map bounds access error:', error);
        }
      };

      // Listen for map movement events with debouncing and error handling
      let timeoutId: NodeJS.Timeout;
      const debouncedBoundsChange = () => {
        try {
          if (map && mapInstanceRef.current) {
            console.log('🎯 Map movement detected, triggering bounds update...');
            clearTimeout(timeoutId);
            timeoutId = setTimeout(handleBoundsChange, 200); // Reduced to 200ms for snappier response
          }
        } catch (error) {
          console.warn('Map movement handler error:', error);
        }
      };

      map.on('moveend', debouncedBoundsChange);
      map.on('zoomend', debouncedBoundsChange);
      map.on('dragend', debouncedBoundsChange);
      
      // Initial bounds trigger after map loads with better timing
      setTimeout(() => {
        try {
          if (map && map.getContainer() && map.getZoom() > 0) {
            console.log('🚀 Initial bounds trigger');
            handleBoundsChange();
          }
        } catch (error) {
          console.warn('Initial bounds trigger error:', error);
        }
      }, 3000);
    }

    // Store map instance
    mapInstanceRef.current = map;

    // Cleanup function
    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
    })();
  }, []); // Only run once on mount

  // Fit map to externally provided bounds (e.g., from location search)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!mapInstanceRef.current || !targetBounds) return;
    (async () => {
      await ensureLeafletLoaded();
      if (!L || !mapInstanceRef.current || !targetBounds) return;
    const map = mapInstanceRef.current;
    try {
      const bounds = L.latLngBounds(
        [targetBounds.south, targetBounds.west],
        [targetBounds.north, targetBounds.east]
      );
      map.fitBounds(bounds.pad(0.05));
      didFitBoundsRef.current = true;
    } catch (e) {
      console.warn('Failed to fit to target bounds:', e);
    }
  })();
  }, [targetBounds]);

  // Separate useEffect for updating markers when properties change
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!mapInstanceRef.current || !properties.length) return;
    (async () => {
      await ensureLeafletLoaded();
      if (!L || !mapInstanceRef.current || !properties.length) return;

    const map = mapInstanceRef.current;
    
    // Clear existing markers
    markersRef.current.forEach((marker) => map.removeLayer(marker));
    markersRef.current.clear();

    // Add individual markers for all properties
    const markers: L.Marker[] = [];
    properties.forEach(property => {
      const lat = typeof property.latitude === 'string' ? parseFloat(property.latitude) : property.latitude;
      const lng = typeof property.longitude === 'string' ? parseFloat(property.longitude) : property.longitude;
      
      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        const marker = L.marker([lat, lng], {
          icon: L.divIcon({
            html: `
              <div class="red-dot-marker" style="
                background: #dc2626;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                border: 2px solid white;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                cursor: pointer;
              "></div>
            `,
            className: 'custom-red-marker',
            iconSize: [16, 16],
            iconAnchor: [8, 8]
          })
        });

        marker.on('click', () => {
          if (onPropertySelect) {
            onPropertySelect(property);
          }
          
          // Open property detail modal instead of popup for better UX
          if (onScheduleTour && onContactAgent) {
            // This will be handled by the parent component opening the property detail modal
          }
        });

        const galleryHtml = (property.images && property.images.length)
          ? `<div style="position: relative; margin-bottom: 8px;">
               <div style="white-space: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; scroll-snap-type: x mandatory;">
                 ${property.images.map((url: string) => `
                   <img src="${url}"
                        style="display:inline-block; width: 240px; height: 150px; object-fit: cover; border-radius: 8px; margin-right: 8px; scroll-snap-align: start;"
                        onerror="this.style.display='none';" />
                 `).join('')}
               </div>
               <div style="position:absolute; top:50%; left:4px; transform: translateY(-50%); background:rgba(0,0,0,0.5); color:white; width:24px; height:24px; border-radius:9999px; display:flex; align-items:center; justify-content:center; cursor:pointer; user-select:none;" onclick="this.parentElement.querySelector('div').scrollBy({left:-240, behavior:'smooth'})">‹</div>
               <div style="position:absolute; top:50%; right:4px; transform: translateY(-50%); background:rgba(0,0,0,0.5); color:white; width:24px; height:24px; border-radius:9999px; display:flex; align-items:center; justify-content:center; cursor:pointer; user-select:none;" onclick="this.parentElement.querySelector('div').scrollBy({left:240, behavior:'smooth'})">›</div>
             </div>`
          : '';

        const popupContent = `
          <div style="min-width: 240px; max-width: 520px;">
            ${galleryHtml}
            <h3 style="margin: 0 0 8px 0; font-size: 16px; color: #333;">
              ${formatPrice(property.price)}
            </h3>
            <p style="margin: 0 0 4px 0; font-size: 14px; color: #666;">
              ${property.bedrooms || 'N/A'} bed, ${property.bathrooms || 'N/A'} bath
            </p>
            <p style="margin: 0 0 8px 0; font-size: 12px; color: #888;">
              ${property.address || `${property.city}, ${property.state}`}
            </p>
            <div style="text-center; margin-top: 12px;">
              <p style="font-size: 12px; color: #666; margin-bottom: 8px;">Click marker to view details and contact options</p>
            </div>
          </div>
        `;

        marker.bindPopup(popupContent);
        markers.push(marker);
        markersRef.current.set(property.id, marker);
      }
    });

    // Add markers to map
    markers.forEach(marker => marker.addTo(map));
    console.log(`🎯 Updated ${markers.length} property markers`);

    // Fit bounds once on first data load to ensure markers are visible
    if (markers.length > 0 && !didFitBoundsRef.current) {
      const group = new L.FeatureGroup(markers);
      map.fitBounds(group.getBounds().pad(0.1));
      didFitBoundsRef.current = true;
    }
  })();
  }, [properties, onPropertySelect]);

  // Update marker icons when selectedPropertyId changes
  useEffect(() => {
    if (!markersRef.current.size) return;

    // Update all markers to red dots first
    markersRef.current.forEach((marker, propertyId) => {
      const redIcon = L.divIcon({
        html: `
          <div class="red-dot-marker" style="
            background: #dc2626;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            cursor: pointer;
          "></div>
        `,
        className: 'custom-red-marker',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      marker.setIcon(redIcon);
    });

    // Update the selected marker to green with price
    if (selectedPropertyId && markersRef.current.has(selectedPropertyId)) {
      const selectedMarker = markersRef.current.get(selectedPropertyId)!;
      const selectedProperty = properties.find(p => p.id === selectedPropertyId);
      
      if (selectedProperty) {
        const greenIcon = L.divIcon({
          html: `
            <div class="selected-marker" style="
              background: #22c55e;
              color: white;
              padding: 6px 10px;
              border-radius: 20px;
              font-weight: bold;
              font-size: 12px;
              border: 2px solid white;
              box-shadow: 0 3px 8px rgba(0,0,0,0.25);
              white-space: nowrap;
              position: relative;
            ">
              ${formatPrice(selectedProperty.price)}
              <div style="
                position: absolute;
                bottom: -6px;
                left: 50%;
                transform: translateX(-50%);
                width: 0;
                height: 0;
                border-left: 6px solid transparent;
                border-right: 6px solid transparent;
                border-top: 6px solid #22c55e;
              "></div>
            </div>
          `,
          className: 'custom-selected-marker',
          iconSize: [80, 32],
          iconAnchor: [40, 32]
        });
        selectedMarker.setIcon(greenIcon);
      }
    }
  }, [selectedPropertyId, properties]);

  // Update map controls based on modalOpen
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const map = mapInstanceRef.current as any;

    if (modalOpen) {
      if (!originalInteractionState.current) {
        originalInteractionState.current = {
          dragging: map.dragging?.enabled?.() ?? true,
          touchZoom: map.touchZoom?.enabled?.() ?? true,
          scrollWheelZoom: map.scrollWheelZoom?.enabled?.() ?? true,
          doubleClickZoom: map.doubleClickZoom?.enabled?.() ?? true,
          boxZoom: map.boxZoom?.enabled?.() ?? true,
          keyboard: map.keyboard?.enabled?.() ?? true,
          tap: map.tap?.enabled?.(),
        };
      }

      map.dragging?.disable?.();
      map.touchZoom?.disable?.();
      map.scrollWheelZoom?.disable?.();
      map.doubleClickZoom?.disable?.();
      map.boxZoom?.disable?.();
      map.keyboard?.disable?.();
      map.tap?.disable?.();
    } else if (originalInteractionState.current) {
      const state = originalInteractionState.current;
      if (state.dragging) map.dragging?.enable?.();
      if (state.touchZoom) map.touchZoom?.enable?.();
      if (state.scrollWheelZoom) map.scrollWheelZoom?.enable?.();
      if (state.doubleClickZoom) map.doubleClickZoom?.enable?.();
      if (state.boxZoom) map.boxZoom?.enable?.();
      if (state.keyboard) map.keyboard?.enable?.();
      if (state.tap) map.tap?.enable?.();
      originalInteractionState.current = null;
    }
  }, [modalOpen]);

  const switchMapType = (newMapType: 'automatic' | 'satellite') => {
    if (!mapInstanceRef.current || !currentTileLayerRef.current) return;
    
    // Remove current tile layer
    mapInstanceRef.current.removeLayer(currentTileLayerRef.current);
    
    // Add new tile layer based on selection
    let newLayer: L.TileLayer;
    
    if (newMapType === 'satellite') {
      // Use hybrid satellite view with labels
      newLayer = L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
        attribution: '&copy; Google',
        maxZoom: 20,
        className: 'map-tiles-satellite'
      });
    } else {
      newLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
        className: 'map-tiles-automatic'
      });
    }
    
    newLayer.addTo(mapInstanceRef.current);
    currentTileLayerRef.current = newLayer;
    setMapType(newMapType);
    setIsDropdownOpen(false);
  };

  const formatPrice = (price: number | string): string => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (!numPrice) return 'Price on request';
    if (numPrice >= 1000000) {
      return `$${(numPrice / 1000000).toFixed(1)}M`;
    } else if (numPrice >= 1000) {
      return `$${(numPrice / 1000).toFixed(0)}K`;
    }
    return `$${numPrice.toLocaleString()}`;
  };

  return (
    <div className={`relative ${className}`}>
      <div 
        ref={mapRef} 
        style={{ height, width: '100%' }}
        className="rounded-lg overflow-hidden shadow-lg"
      />
      
      {/* Property count overlay */}
      <div className="absolute bottom-4 left-4 bg-white px-3 py-2 rounded-lg shadow-lg border z-[1000]">
        <p className="text-sm font-medium text-gray-700">
          {properties.filter(p => p.latitude && p.longitude).length} Properties Found
        </p>
      </div>

      {/* Drawing Controls */}
      <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
        <button
          onClick={() => {
            const drawControl = drawControlRef.current;
            const map = mapInstanceRef.current;
            if (drawControl && map) {
              if (!isDrawMode) {
                map.addControl(drawControl);
                setIsDrawMode(true);
              } else {
                map.removeControl(drawControl);
                setIsDrawMode(false);
              }
            }
          }}
          className={`bg-white border border-gray-300 rounded-lg p-3 shadow-lg hover:bg-gray-50 transition-colors ${
            isDrawMode ? 'bg-ocean-blue text-white hover:bg-bright-blue border-ocean-blue' : ''
          }`}
          title="Toggle drawing tools for precise area search"
          data-testid="button-toggle-draw-mode"
        >
          <Square className="w-5 h-5" />
        </button>
        
        {isDrawMode && (
          <button
            onClick={() => {
              const drawnLayers = drawnLayersRef.current;
              if (drawnLayers) {
                drawnLayers.clearLayers();
                if (onDrawnArea) {
                  onDrawnArea(null);
                }
              }
            }}
            className="bg-red-500 text-white border border-red-600 rounded-lg p-3 shadow-lg hover:bg-red-600 transition-colors"
            title="Clear drawn search area"
            data-testid="button-clear-drawn-area"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Map Type Selector Dropdown */}
      <div className="absolute bottom-4 right-4 z-[1000]">
        <div className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="bg-white border border-gray-300 rounded-lg px-4 py-2 shadow-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm font-medium text-gray-700"
            data-testid="button-map-type-selector"
          >
            {mapType === 'automatic' ? 'Map' : 'Satellite'}
            <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isDropdownOpen && (
            <div className="absolute bottom-full mb-2 right-0 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden min-w-[120px]">
              <button
                onClick={() => switchMapType('automatic')}
                className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 transition-colors ${
                  mapType === 'automatic' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                }`}
                data-testid="button-map-automatic"
              >
                Map
              </button>
              <button
                onClick={() => switchMapType('satellite')}
                className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 transition-colors border-t border-gray-100 ${
                  mapType === 'satellite' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                }`}
                data-testid="button-map-satellite"
              >
                Satellite
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Custom CSS for property markers and modern map */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .map-tiles-automatic {
            filter: contrast(1.1) saturate(1.15) brightness(1.02);
          }
          .map-tiles-satellite {
            filter: contrast(1.1) saturate(1.1);
          }
          .custom-red-marker, .custom-selected-marker, .custom-hover-marker {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
          .red-dot-marker {
            transition: all 0.2s ease;
            cursor: pointer;
          }
          .red-dot-marker:hover {
            transform: scale(1.2);
            box-shadow: 0 3px 6px rgba(0,0,0,0.4);
          }
          .selected-marker {
            animation: gentleBounceIn 0.4s ease;
          }
          @keyframes gentleBounceIn {
            0% { transform: translateY(-10px) scale(0.9); opacity: 0; }
            50% { transform: translateY(-12px) scale(1.02); opacity: 0.9; }
            100% { transform: translateY(-10px) scale(1); opacity: 1; }
          }
          .leaflet-popup-content-wrapper {
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.15);
          }
          .leaflet-popup-tip {
            background: white;
          }
          .leaflet-container {
            background: #f8f9fa;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          .leaflet-control-zoom {
            border: none !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
          }
          .leaflet-control-zoom a {
            border-radius: 6px !important;
            border: none !important;
            box-shadow: none !important;
          }
          .leaflet-control-attribution {
            background: rgba(255,255,255,0.8) !important;
            border-radius: 4px !important;
            font-size: 10px !important;
          }
        `
      }} />
    </div>
  );
};

export default PropertyMap;